import React, { useState, useEffect } from 'react';
import { Table, Button, Container, Row, Col, Spinner, Form, Card } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css'; 
import { useAuth } from '../../Utils/Auth';
import AccountsService from '../../Services/AccountsService';
import NewPagination from '../../Component/NewPagination';
import { useNavigate } from 'react-router-dom';

// ✅ Custom hook for debouncing
function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const AffiliateUsersList = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationData, setPaginationData] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10,
  });

  const auth = useAuth();
  const navigate = useNavigate();


  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  function fetchData(page) {
    setLoading(true);
    AccountsService.getIntroducerUser(
      auth.user.introId,
      auth.user,
      page || paginationData.page,
      paginationData.limit,
      debouncedSearchTerm
    )
      .then((res) => {
        setAffiliates(res?.data?.data);
        setPaginationData({
          page: res?.data?.pagination?.page,
          totalPages: res?.data?.pagination?.totalPages,
          totalItems: res?.data?.pagination?.totalItems,
          limit: res?.data?.pagination?.limit
        });
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching network data:", error);
        setLoading(false);
      });
  }

 
  useEffect(() => {
    fetchData();
  }, [debouncedSearchTerm]);

  const handleViewTransactions = (affiliate) => {
    navigate(`/customerStatement/${affiliate.userId}`);
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading Networks...</span>
      </div>
    );
  }

  const startIndex = Math.min((paginationData.page - 1) * paginationData.limit + 1);
  const endIndex = Math.min(paginationData.page * paginationData.limit, paginationData.totalItems);

  const onPageChange = (selectedPage) => {
    fetchData(selectedPage);
  };

  return (
    <Container fluid className="affiliate-container">
      <Row className="mb-4">
        <Col>
          <h1 className="page-title">My Network</h1>
          <Button variant="outline-secondary" onClick={() => navigate("/welcome")}>
            <i className="fas fa-arrow-left "></i>
          </Button>
        </Col>
      </Row>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="filter-row">
            <Col md={6}>
              <Form.Group controlId="searchAffiliates" className="search-box">
                <Form.Control
                  type="text"
                  placeholder="Search Customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="bi bi-search search-icon"></i>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th className="ps-4">Customers</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.length > 0 ? (
                  affiliates.map((affiliate) => (
                    <tr key={affiliate.id} className="affiliate-row">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div className="avatar me-3 wth-bg">
                            {affiliate.userName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold">{`${affiliate.firstName} ${affiliate.lastName}`}</div>
                            <div className="text-muted small">@{affiliate.userName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-end pe-4">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleViewTransactions(affiliate)}
                        >
                          Transactions
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <div className="empty-state">
                        <i className="bi bi-people empty-icon"></i>
                        <h5>No affiliates found</h5>
                        <p className="text-muted">
                          {searchTerm || statusFilter !== 'all'
                            ? 'Try adjusting your search or filter criteria'
                            : 'You currently have no affiliates'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {affiliates.length > 0 && (
        <NewPagination
          currentPage={paginationData.page}
          totalPages={paginationData.totalPages}
          handlePageChange={onPageChange}
          startIndex={startIndex}
          endIndex={endIndex}
          totalData={paginationData.totalItems}
        />
      )}
    </Container>
  );
};

export default AffiliateUsersList;
